import React, { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, IndianRupee, Calendar, ChevronDown } from 'lucide-react';

const SalaryTrajectory = ({ roadmaps = [] }) => {
    const [selectedRoadmap, setSelectedRoadmap] = useState(null);
    const [timeframe, setTimeframe] = useState(3); // years
    const [hoverYear, setHoverYear] = useState(null);

    React.useEffect(() => {
        if (roadmaps.length > 0 && !selectedRoadmap) {
            setSelectedRoadmap(roadmaps[0]);
        }
    }, [roadmaps]);

    const salaryData = useMemo(() => {
        const careerPath = selectedRoadmap?.careerPath?.toLowerCase() || '';
        let baseSalary = 600000; // 6 LPA default

        // Estimate based on career path
        if (careerPath.includes('data scientist') || careerPath.includes('ml engineer') || careerPath.includes('machine learning')) {
            baseSalary = 800000;
        } else if (careerPath.includes('software') || careerPath.includes('developer') || careerPath.includes('engineer')) {
            baseSalary = 700000;
        } else if (careerPath.includes('product manager') || careerPath.includes('pm')) {
            baseSalary = 900000;
        } else if (careerPath.includes('design') || careerPath.includes('ux')) {
            baseSalary = 650000;
        } else if (careerPath.includes('devops') || careerPath.includes('cloud')) {
            baseSalary = 750000;
        } else if (careerPath.includes('cyber') || careerPath.includes('security')) {
            baseSalary = 800000;
        }

        const progress = (selectedRoadmap?.progress?.progressPercentage || 0) / 100;
        const growthRates = [0, 0.15, 0.25, 0.20, 0.18, 0.15]; // Year 0-5 growth rates

        const years = [];
        let currentSalary = baseSalary;
        let withSkillsSalary = baseSalary * (1 + progress * 0.1);

        for (let i = 0; i <= 5; i++) {
            const growthRate = growthRates[i] || 0.12;
            if (i > 0) {
                currentSalary *= (1 + 0.08); // Without roadmap: 8% annual
                withSkillsSalary *= (1 + growthRate); // With roadmap: higher growth
            }
            years.push({
                year: i,
                label: i === 0 ? 'Now' : `Year ${i}`,
                without: Math.round(currentSalary),
                with: Math.round(withSkillsSalary),
                diff: Math.round(withSkillsSalary - currentSalary)
            });
        }

        return years.slice(0, timeframe + 1);
    }, [selectedRoadmap, timeframe]);

    const formatSalary = (amount) => {
        if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(1)}Cr`;
        if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
        return `₹${(amount / 1000).toFixed(0)}K`;
    };

    const maxSalary = Math.max(...salaryData.map(d => d.with));
    const chartHeight = 180;
    const chartWidth = 100; // percentage basis

    const projectedGain = salaryData[salaryData.length - 1]?.diff || 0;
    const gainPercentage = salaryData.length > 1
        ? Math.round(((salaryData[salaryData.length - 1].with / salaryData[0].with) - 1) * 100)
        : 0;

    if (roadmaps.length === 0) return null;

    return (
        <div className="bg-[#0F131F] border border-white/5 rounded-2xl p-6">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-400" />
                    <h3 className="font-bold text-white text-sm">Salary Trajectory</h3>
                </div>
                <div className="flex items-center gap-2">
                    {[1, 3, 5].map(y => (
                        <button
                            key={y}
                            onClick={() => setTimeframe(y)}
                            className={`px-2 py-1 text-[10px] font-mono rounded transition-colors ${timeframe === y
                                    ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                                    : 'text-slate-500 hover:text-slate-300'
                                }`}
                        >
                            {y}Y
                        </button>
                    ))}
                </div>
            </div>

            {/* Highlight Cards */}
            <div className="grid grid-cols-2 gap-3 mb-6">
                <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-xl p-3">
                    <div className="text-xs text-slate-500 font-mono mb-1">PROJECTED IN {timeframe}Y</div>
                    <div className="text-xl font-bold text-emerald-400">
                        {formatSalary(salaryData[salaryData.length - 1]?.with || 0)}
                    </div>
                </div>
                <div className="bg-cyan-500/5 border border-cyan-500/10 rounded-xl p-3">
                    <div className="text-xs text-slate-500 font-mono mb-1">EXTRA EARNINGS</div>
                    <div className="text-xl font-bold text-cyan-400 flex items-center gap-1">
                        +{formatSalary(projectedGain)}
                        <span className="text-xs text-slate-500">/yr</span>
                    </div>
                </div>
            </div>

            {/* Chart Area */}
            <div className="relative" style={{ height: chartHeight + 40 }}>
                <svg width="100%" height={chartHeight + 40} viewBox={`0 0 400 ${chartHeight + 40}`} preserveAspectRatio="none">
                    {/* Grid lines */}
                    {[0, 25, 50, 75, 100].map((pct) => (
                        <line
                            key={pct}
                            x1="40"
                            y1={chartHeight - (chartHeight * pct) / 100 + 10}
                            x2="390"
                            y2={chartHeight - (chartHeight * pct) / 100 + 10}
                            stroke="rgba(255,255,255,0.03)"
                            strokeWidth="1"
                        />
                    ))}

                    {/* "Without" area */}
                    <defs>
                        <linearGradient id="withoutGrad" x1="0" x2="0" y1="0" y2="1">
                            <stop offset="0%" stopColor="rgba(148, 163, 184, 0.1)" />
                            <stop offset="100%" stopColor="rgba(148, 163, 184, 0)" />
                        </linearGradient>
                        <linearGradient id="withGrad" x1="0" x2="0" y1="0" y2="1">
                            <stop offset="0%" stopColor="rgba(16, 185, 129, 0.15)" />
                            <stop offset="100%" stopColor="rgba(16, 185, 129, 0)" />
                        </linearGradient>
                    </defs>

                    {/* Without roadmap area */}
                    <path
                        d={`M ${salaryData.map((d, i) => {
                            const x = 40 + (i / (salaryData.length - 1)) * 350;
                            const y = chartHeight - (d.without / maxSalary) * (chartHeight - 20) + 10;
                            return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
                        }).join(' ')} L ${40 + 350} ${chartHeight + 10} L 40 ${chartHeight + 10} Z`}
                        fill="url(#withoutGrad)"
                    />

                    {/* With roadmap area */}
                    <path
                        d={`M ${salaryData.map((d, i) => {
                            const x = 40 + (i / (salaryData.length - 1)) * 350;
                            const y = chartHeight - (d.with / maxSalary) * (chartHeight - 20) + 10;
                            return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
                        }).join(' ')} L ${40 + 350} ${chartHeight + 10} L 40 ${chartHeight + 10} Z`}
                        fill="url(#withGrad)"
                    />

                    {/* Without roadmap line */}
                    <path
                        d={salaryData.map((d, i) => {
                            const x = 40 + (i / (salaryData.length - 1)) * 350;
                            const y = chartHeight - (d.without / maxSalary) * (chartHeight - 20) + 10;
                            return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
                        }).join(' ')}
                        fill="none"
                        stroke="rgba(148, 163, 184, 0.3)"
                        strokeWidth="1.5"
                        strokeDasharray="4 4"
                    />

                    {/* With roadmap line */}
                    <motion.path
                        d={salaryData.map((d, i) => {
                            const x = 40 + (i / (salaryData.length - 1)) * 350;
                            const y = chartHeight - (d.with / maxSalary) * (chartHeight - 20) + 10;
                            return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
                        }).join(' ')}
                        fill="none"
                        stroke="#10b981"
                        strokeWidth="2.5"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 1.5, ease: "easeInOut" }}
                    />

                    {/* Data points with salary labels */}
                    {salaryData.map((d, i) => {
                        const x = 40 + (i / (salaryData.length - 1)) * 350;
                        const yWith = chartHeight - (d.with / maxSalary) * (chartHeight - 20) + 10;
                        const isHovered = hoverYear === i;
                        return (
                            <g key={i}>
                                {/* Hover area */}
                                <rect
                                    x={x - 25}
                                    y="0"
                                    width="50"
                                    height={chartHeight + 40}
                                    fill="transparent"
                                    onMouseEnter={() => setHoverYear(i)}
                                    onMouseLeave={() => setHoverYear(null)}
                                    style={{ cursor: 'pointer' }}
                                />
                                {/* Dot */}
                                <circle cx={x} cy={yWith} r={isHovered ? 6 : 4} fill="#10b981" stroke="#0B0F19" strokeWidth="2" />
                                {/* Year label */}
                                <text x={x} y={chartHeight + 30} textAnchor="middle" fill="#475569" fontSize="10" fontFamily="monospace">
                                    {d.label}
                                </text>
                                {/* Salary tooltip */}
                                {isHovered && (
                                    <>
                                        <rect x={x - 35} y={yWith - 28} width="70" height="20" rx="4" fill="#0B0F19" stroke="rgba(16,185,129,0.3)" />
                                        <text x={x} y={yWith - 14} textAnchor="middle" fill="#10b981" fontSize="10" fontWeight="bold" fontFamily="monospace">
                                            {formatSalary(d.with)}
                                        </text>
                                    </>
                                )}
                            </g>
                        );
                    })}
                </svg>
            </div>

            {/* Legend */}
            <div className="flex items-center justify-between mt-4 text-xs">
                <div className="flex items-center gap-4">
                    <span className="flex items-center gap-2">
                        <span className="w-4 h-0.5 bg-emerald-500 rounded"></span>
                        <span className="text-slate-400">With Rastor AI</span>
                    </span>
                    <span className="flex items-center gap-2">
                        <span className="w-4 h-0.5 bg-slate-500 rounded" style={{ borderBottom: '1px dashed' }}></span>
                        <span className="text-slate-500">Without</span>
                    </span>
                </div>
                <span className="text-emerald-400 font-mono font-bold">+{gainPercentage}% in {timeframe}y</span>
            </div>
        </div>
    );
};

export default SalaryTrajectory;
